import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import { DiscountFormV2StateType, CouponType, EntitledCatalogueEntityType } from 'src/lib/types/discountsV2'
import { getItemByIds, getVariantsByIds } from 'src/lib/core/api-methods'
import { catalogueAdapter } from 'src/lib/utils/adapters'

type IdsByType = {
  productIds: Set<string>
  collectionIds: Set<string>
  variantIds: Set<string>
}

const collectPlaceholderIds = (
  groups: CatalogueDataType[][] | undefined,
  bucket: IdsByType
): void => {
  ;(groups ?? []).forEach(group => {
    ;(group ?? []).forEach(item => {
      if (!item?.isPlaceholder || !item?.id) return
      switch (item.type) {
        case EntitledCatalogueEntityType.COLLECTION:
          bucket.collectionIds.add(item.id)
          break
        case EntitledCatalogueEntityType.VARIANT:
          bucket.variantIds.add(item.id)
          break
        default:
          bucket.productIds.add(item.id)
      }
    })
  })
}

const swapPlaceholders = (
  groups: CatalogueDataType[][] | undefined,
  lookup: Map<string, CatalogueDataType>
): CatalogueDataType[][] | undefined => {
  if (!groups) return groups
  return groups.map(group =>
    (group ?? []).map(item => {
      if (!item?.isPlaceholder || !item?.id) return item
      const enriched = lookup.get(item.id)
      return enriched ?? item
    })
  )
}

// After parseDiscountResponseV2 seeds the form with placeholder chips (id-only) for exclusion
// entities whose display metadata wasn't stored, fetch the real product/collection/variant data
// and rewrite those chips so they render with name + image, the same way they look when the
// merchant first picks them.
export const enrichExcludedCataloguePlaceholders = async (
  state: DiscountFormV2StateType,
  issuerId: string,
  merchantId: string | undefined
): Promise<DiscountFormV2StateType | null> => {
  const bucket: IdsByType = {
    productIds: new Set(),
    collectionIds: new Set(),
    variantIds: new Set()
  }

  const entitlementExcluded =
    state.entitlement?.type !== CouponType.SHIPPING
      ? state.entitlement?.metaData?.excludedCatalogue
      : undefined
  const prerequisiteExcluded =
    state.prerequisite?.type !== CouponType.BUNDLE
      ? state.prerequisite?.metaData?.excludedCatalogue
      : undefined

  collectPlaceholderIds(entitlementExcluded, bucket)
  collectPlaceholderIds(prerequisiteExcluded, bucket)

  if (bucket.productIds.size === 0 && bucket.collectionIds.size === 0 && bucket.variantIds.size === 0) {
    return null
  }

  const [productRows, collectionRows, variantRows] = await Promise.all([
    bucket.productIds.size > 0
      ? (getItemByIds(issuerId, merchantId, Array.from(bucket.productIds), 'PRODUCT') as Promise<any[]>)
      : Promise.resolve([]),
    bucket.collectionIds.size > 0
      ? (getItemByIds(issuerId, merchantId, Array.from(bucket.collectionIds), 'COLLECTION') as Promise<any[]>)
      : Promise.resolve([]),
    bucket.variantIds.size > 0
      ? (getVariantsByIds(issuerId, merchantId, Array.from(bucket.variantIds)) as Promise<any[]>)
      : Promise.resolve([])
  ])

  const lookup = new Map<string, CatalogueDataType>()
  ;(productRows ?? []).forEach((row: any) => {
    const adapted = catalogueAdapter(row, 'PRODUCT') as CatalogueDataType | undefined
    if (adapted?.id) lookup.set(adapted.id, adapted)
  })
  ;(collectionRows ?? []).forEach((row: any) => {
    const adapted = catalogueAdapter(row, 'COLLECTION') as CatalogueDataType | undefined
    if (adapted?.id) lookup.set(adapted.id, adapted)
  })
  ;(variantRows ?? []).forEach((row: any) => {
    const adapted = catalogueAdapter(row, 'VARIANT') as CatalogueDataType | undefined
    if (adapted?.id) lookup.set(adapted.id, adapted)
  })

  if (lookup.size === 0) return null

  const nextEntitlementExcluded = swapPlaceholders(entitlementExcluded, lookup)
  const nextPrerequisiteExcluded = swapPlaceholders(prerequisiteExcluded, lookup)

  const nextEntitlement =
    state.entitlement?.type !== CouponType.SHIPPING && nextEntitlementExcluded
      ? {
          ...state.entitlement,
          metaData: {
            ...state.entitlement.metaData,
            excludedCatalogue: nextEntitlementExcluded
          }
        }
      : state.entitlement

  const nextPrerequisite =
    state.prerequisite?.type !== CouponType.BUNDLE && nextPrerequisiteExcluded
      ? {
          ...state.prerequisite,
          metaData: {
            ...state.prerequisite.metaData,
            excludedCatalogue: nextPrerequisiteExcluded
          }
        }
      : state.prerequisite

  return {
    ...state,
    entitlement: nextEntitlement,
    prerequisite: nextPrerequisite
  } as DiscountFormV2StateType
}
